import React from "react";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import { ProductCheckboxList } from "components/ProductCheckboxList";
import { getInvoiceV1FormSections } from "invoice/getInvoiceV1FormSections";
import { useDocumentV1Form } from "documentV1/hooks";
import { DocumentNumber } from "components/Document/DocumentNumber";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { DocumentTypeWithoutInvoice, DocumentTypeWithoutUnknown, getDocumentRoutes, getEnabledV1DocumentTypes, getNewLabelForDocumentType } from "utilities/documentUtils";
import { getDocumentCoreApiExceptInvoice } from "utilities/apiUtils";
import { mapDocumentToFormState, mapInvoiceToFormState } from "documentV1/detail/documentFormState";
import { getDocumentV1FormSections } from "documentV1/getDocumentV1FormSections";

interface Props {
  products: server.dto.Product[]
  document: server.dto.InvoiceV1 | server.dto.DocumentV1
  artistRiders: server.dto.ArtistRider[]
}

export const DocumentCopyEditSection = (props: Props) => {
  const doc = props.document;
  const isInvoice = "invoiceId" in doc;
  const documentId = isInvoice ? doc.invoiceId : doc.documentId;
  const helper = isInvoice ? mapInvoiceToFormState(doc) : mapDocumentToFormState(doc);

  const [documentTypeToCopyTo, setDocumentTypeToCopyTo] = React.useState<server.dto.DocumentTypes>(isInvoice ? "invoice" : doc.documentType);
  const [selectedProducts, setSelectedProducts] = React.useState<number[]>(props.products.map(p => p.productId));

  const hook = useDocumentV1Form({
    documentType: documentTypeToCopyTo,
    initialAddressee: helper.addresseeCompany,
    initialContact: helper.contact,
    initialOwner: helper.owner,
    initialConcerning: helper.subject || "",
    initialCostCenter: helper.costCenter,
    allArtistRiders: props.artistRiders,
    initialBookkeeping: doc.bookkeeping,
    initialCurrency: doc.currency,
    initialExchangeRate: doc.exchangeRate,
  });

  const onDocumentTypeChange = (type: DocumentTypeWithoutUnknown) => {
    setDocumentTypeToCopyTo(type);
    hook.setIsDraft(type === "invoice" ? site.currentEnvironment.invoice.legacyDefaultDraft : false);
  }

  const submit = async () => {
    if (documentTypeToCopyTo === "invoice") {
      return window.CoreApi.Invoice.Copy({
        sourceDocumentId: documentId,
        productIds: selectedProducts,
        invoice: {
          isDraft: hook.isDraft,
          bookkeepingId: hook.bookkeeping!.bookkeepingId,
          ownerUserId: hook.owner.userId,
          invoiceDate: hook.sendDate.toDate(),
          dueInDays: hook.dueInDays,
          currency: hook.currency,
          exchangeRate: hook.exchangeRate,
          concerning: hook.concerning,
          addresseeCompanyId: hook.addressee?.companyId,
          contactId: hook.contact?.contactId,
          bankAccountId: hook.bankAccount?.bankAccountId,
          costCenterId: hook.costCenter?.costCenterId,
        } satisfies Required<server.dto.CreateInvoiceV1Instruction>
      } satisfies Required<server.dto.CopyInvoiceInstruction>);
    } else {
      const instruction = {
        sourceDocumentId: documentId,
        productIds: selectedProducts,
        document: {
          documentType: documentTypeToCopyTo,
          artistRiderId: hook.artistRider?.artistRiderId,
          bookkeepingId: hook.bookkeeping.bookkeepingId,
          addresseeCompanyId: hook.addressee.companyId,
          contactId: hook.contact?.contactId,
          currency: hook.currency,
          dueInDays: hook.dueInDays,
          exchangeRate: hook.exchangeRate,
          isDraft: hook.isDraft,
          ownerUserId: hook.owner.userId,
          sendDate: hook.sendDate.toDate(),
          concerning: hook.concerning,
        } satisfies Required<server.dto.CreateDocumentV1Instruction>,
      } satisfies Required<server.dto.CopyDocumentV1Instruction>;

      const apis = getDocumentCoreApiExceptInvoice(documentTypeToCopyTo);
      return apis.Copy(instruction);
    }
  };

  const documentTypes = getEnabledV1DocumentTypes();

  const sections = documentTypeToCopyTo === "invoice"
    ? getInvoiceV1FormSections(hook, { title: getNewLabelForDocumentType(documentTypeToCopyTo), })
    : getDocumentV1FormSections(hook, { documentType: documentTypeToCopyTo as DocumentTypeWithoutInvoice });

  return <EditSectionTemplate
    submit={submit}
    redirectUrl={(itemId) => {
      const documentRoutes = getDocumentRoutes(documentTypeToCopyTo, itemId);
      return documentRoutes.detail;
    }}
    sections={[
      {
        key: "documentNumberAndType",
        type: "regular",
        title: RESX.GeneralLabels.Number,
        fields: [{
          key: "documentNumber",
          label: RESX.GeneralLabels.Number,
          renderEditNew: () => (
            <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
              <DocumentNumber documentNumber={doc.documentNumber} locked={doc.locked} />
            </div>
          )
        }, {
          key: "documentType",
          label: RESX.GeneralLabels.Type,
          renderEditNew: () => (
            <RadioGroup
              orientation="vertical"
              options={documentTypes.map(o => ({ label: o.text, value: o.type }))}
              value={documentTypeToCopyTo}
              onChange={onDocumentTypeChange}
            />
          )
        }]
      },
      sections,
      {
        key: "products",
        type: "regular",
        hidden: props.products.length === 0,
        title: RESX.Product.Products,
        fields: [{
          key: "products",
          label: RESX.Product.Products,
          renderEditNew: () => (
            <ProductCheckboxList
              products={props.products}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
            />
          )
        }]
      }]} />;
};

export default DocumentCopyEditSection;

